/*
The Store plugin can be used to persist corrections to a database running on your server.
It has a simple customisable interface that can be implemented with any web framework.
It works by listening to events published by the Corrector and making appropriate requests to the server depending on the event.

The store handles five distinct actions "read", "search", "create", "update" and "destory".
The requests made can be customised with options when the plugin is added to the Corrector.
Custom headers can also be sent with every request by setting a $.data key on the Correction#element containing headers to send.
e.g:
corrector.element.data('corrector:headers', {
	'X-My-Custom-Header': 'MyCustomValue'
})
// This header will now be sent with every request.

*/
class CorrectionsStorePlugin extends CorrectorPlugin {


	///////////////////////////////////////////////////////////////////////////////////////////
	//  CONSTRUCTOR
	///////////////////////////////////////////////////////////////////////////////////////////

	/*
	CONSTRUCTOR: The contsructor initailases the Store instance.
	It requires the Corrector#element and an Object of options.

	element - This must be the Corrector#element in order to listen for events.
	options - An Object of key/value user options.

	Examples

	store = new CorrectionsStorePlugin(Corrector.element, {
		prefix: 'https://mysite.com',
		correctionData: {
			uri: window.location.href
		}
	})

	Returns a new instance of CorrectionsStorePlugin.
	*/
	constructor(element, options = {}) {
		// User customisable options available.
		const defaultOptions = {

			// Custom meta data that will be attached to every correction that is sent to the server. This _will_ override previous values.
			correctionData: {},

			/*
			Should the plugin emulate HTTP methods like PUT and DELETE for interaction with legacy web servers?
			Setting this to `true` will fake HTTP `PUT` and `DELETE` requests with an HTTP `POST`,
			and will set the request header `X-HTTP-Method-Override` with the name of the desired method.
			*/
			emulateHTTP: false,

			/*
			If loadFromSearch is set, then we load the first batch of corrections from the 'search' URL as set in `options.urls` instead of the registry path 'prefix/read'
			loadFromSearch: {
				'limit': 0,
				'all_fields': 1
				'uri': 'http://this/document/only'
			}
			*/
			loadFromSearch: false,

			/*
			This is the API endpoint. If the server supports Cross Origin Resource Sharing (CORS) a full URL can be used here.
			*/
			prefix: '/store',

			/*
			The server URLs for each available action.
			These URLs can be anything but must respond to the appropraite HTTP method.
			The token ":id" can be used anywhere in the URL and will be replaced with the correction id.

			read:    GET
			create:  POST
			update:  PUT
			destroy: DELETE
			search:  GET
			*/
			urls: {
				create:  '/corrections',
				read:    '/corrections/:id',
				update:  '/corrections/:id',
				destroy: '/corrections/:id',
				search:  '/search'
			}

		};

		super(element, $.extend(defaultOptions, options));
	}

	properties() {
		super.properties();

		this.corrections = [];

		/*
		The store listens for the following events published by the Corrector.
			- correctionCreated: A new correction has been created.
			- correctionUpdated: An correction has been updated.
			- correctionDeleted: An correction has been deleted.
		*/
		this.events = this.events || {};
		this.events = $.extend(this.events, {
			'correctionCreated': 'correctionCreated',
			'correctionDeleted': 'correctionDeleted',
			'correctionUpdated': 'correctionUpdated'
		});
	}


	///////////////////////////////////////////////////////////////////////////////////////////
	//  PRIVATE METHODS
	///////////////////////////////////////////////////////////////////////////////////////////

	/*
	Checks the loadFromSearch option and if present loads corrections using the Store#loadCorrectionsFromSearch method rather than Store#loadCorrections.

	Returns nothing.
	*/
	_getCorrections() {
		if (this.options.loadFromSearch) {
			this.loadCorrectionsFromSearch(this.options.loadFromSearch);
		}
		else {
			this.loadCorrections();
		}
	}

	/*
	Builds an options object suitable for use in a jQuery.ajax() call.

	action    - The action String eg. "read", "search", "create", "update" or "destroy"
	obj       - The data to be sent, either correction object or query string.
	onSuccess - A callback Function to call on successful request.

	Also extracts any custom headers from data stored on the Corrector#element under the 'corrector:headers' key.
	These headers should be stored as key/value pairs and will be sent with every request..

	Examples:
	corrector.element.data('corrector:headers', {
		'X-My-Custom-Header': 'CustomValue',
		'X-Auth-User-Id': 'bill'
	})

	Returns Object literal of $.ajax() options.
	*/
	_apiRequestOptions(action, obj, onSuccess) {
		const method = this._methodFor(action);

		let opts = {
			type:       method,
			headers:    this.element.data('corrector:headers'),
			dataType:   "json",
			success:    (onSuccess.bind(this) || function() {}),
			error:      this._onError
		};

		// If emulateHTTP is enabled, we send a POST and put the real method in an
		// HTTP request header.
		if (this.options.emulateHTTP && ['PUT', 'DELETE'].includes(method)) {
			opts.headers = $.extend(opts.headers, {'X-HTTP-Method-Override': method});
			opts.type = 'POST';
		}

		// Don't JSONify obj if making search request.
		if (action === "search") {
			opts = $.extend(opts, {data: obj});
			return opts;
		}

		const data = obj && this._dataFor(obj);

		// If emulateJSON is enabled, we send a form request (the correct
		// contentType will be set automatically by jQuery), and put the
		// JSON-encoded payload in the "json" key.
		if (this.options.emulateJSON) {
			opts.data = {json: data};
			if (this.options.emulateHTTP) {
				opts.data._method = method;
			}
			return opts;
		}

		opts = $.extend(opts, {
			data,
			contentType: "application/json; charset=utf-8"
		});
		return opts;
	}

	/*
	Builds the appropriate URL from the options for the action provided.

	action - The action String.
	id     - The correction id as a String or Number.

	Examples:

	store._urlFor('update', 34)
	// Returns "/store/corrections/34"

	store._urlFor('search')
	// Returns "/store/search"

	Returns URL String.
	*/
	_urlFor(action, id) {
		let url = (this.options.prefix != null) ? this.options.prefix : '';
		url += this.options.urls[action];
		// If there's a '/:id' in the URL, either fill in the ID or remove the
		// slash:
		url = url.replace(/\/:id/, (id != null) ? '/' + id : '');
		// If there's a bare ':id' in the URL, then substitute directly:
		url = url.replace(/:id/, (id != null) ? id : '');

		return url;
	}

	/*
	Maps an action to an HTTP method.

	action - The action String

	Examples:

	store._methodFor('read')    // "GET"
	store._methodFor('update')  // "PUT"
	store._methodFor('destroy') // "DELETE"

	Returns HTTP method String.
	*/
	_methodFor(action) {
		const table = {
			'create':  'POST',
			'read':    'GET',
			'update':  'PUT',
			'destroy': 'DELETE',
			'search':  'GET'
		};
		return table[action];
	}

	/*
	Creates a JSON serialisation of an correction.

	correction - An correction Object to serialise.

	Examples:
	store._dataFor({id: 32, text: 'my correction comment'})
    // Returns '{"id": 32, "text":"my correction comment"}'

    Returns JSON string
	*/
	_dataFor(correction) {
		// Store a reference to the highlights array. We can't serialize
		// a list of HTMLElement objects.
		const {
			highlights
		} = correction;

		delete correction.highlights;

		// Preload with extra data.
		$.extend(correction, this.options.correctionData);
		const data = JSON.stringify(correction);

		// Restore the highlights array.
		if (highlights) { correction.highlights = highlights; }

		return data;
	}


	///////////////////////////////////////////////////////////////////////////////////////////
	//  PUBLIC METHODS
	///////////////////////////////////////////////////////////////////////////////////////////

	/*
	PUBLIC: Initialises the plugin and loads the latest corrections.
	If the Auth plugin is also present it will request an auth token before loading any corrections.

	Examples:

	store.pluginInit()

	Returns nothing.
	*/

	pluginInit() {
		if (!Corrector.supported()) {
			return;
		}

		if (this.corrector.plugins.Auth) {
			this.corrector.plugins.Auth.withToken(this._getCorrections);
		}
		else {
			this._getCorrections();
		}
	}

	/*
	PUBLIC: Registers an correction with the Store.
	Used to check whether an correction has already been created when using Store#correctionCreated().

	NB: registerCorrection and unregisterCorrection do no error-checking/ duplication avoidance of their own. Use with care.

	correction - An correction Object to resister

	Examples:

	store.registerCorrection({id: "correction"})

	Returns registered corrections.
	*/
	registerCorrection(correction) {
		return this.corrections.push(correction);
	}

	/*
	PUBLIC: Unregisters an correction with the Store.

	NB: registerCorrection and unregisterCorrection do no error-checking/ duplication avoidance of their own. Use with care.

	correction - An correction Object to unresister.

	Examples:

	store.unregisterCorrection({id: "correction"})

	Returns remaining registered corrections.
	*/
	unregisterCorrection(correction) {
		return this.corrections.splice(this.corrections.indexOf(correction), 1);
	}

	/*
	PUBLIC: Extends the provided correction with the contents of the data Object.
	Will only extend corrections that have been registered with the store.
	Also updates the correction object stored in the 'correction' data store.

	correction - An correction Object to extend.
	data       - An Object containing properties to add to the correction.

	Examples:
	correction = $('.correction-hl:first').data('corrector')
	store.updateCorrection(correction, {extraProperty: "bacon sarnie"})
	console.log($('.correction-hl:first').data('corrector').extraProperty)
	// Outputs "bacon sarnie"

	Returns nothing.
	*/
	updateCorrection(correction, data) {
		if (!this.corrections.includes(correction)) {
			console.error(Corrector._t("Trying to update unregistered correction!"));
		} else {
			$.extend(correction, data);
		}

		// Update the elements with our copies of the correction objects (e.g.
		// with ids from the server).
		$(correction.highlights).data('corrector', correction);
	}

	/*
	PUBLIC: Makes a request to the server for all corrections.

	Examples:
	store.loadCorrections()

	Returns nothing.
	*/
	loadCorrections() {
		this._apiRequest('read', null, this._onLoadCorrections);
	}

	/*
	PUBLIC: Performs the same task as Store.#loadCorrections() but calls the 'search' URI with an optional query string.

	searchOptions - Object literal of query string parameters.

	Examples:
	store.loadCorrectionsFromSearch({
		limit: 100,
		uri: window.location.href
	})

	Returns nothing.
	*/
	loadCorrectionsFromSearch(searchOptions) {
		this._apiRequest('search', searchOptions, this._onLoadCorrectionsFromSearch);
	}

	/*
	PUBLIC: Dump an array of serialized corrections

	param - comment

	// TODO: Should it return?
	*/
	dumpCorrections() {
		return (this.corrections.map((ann) => JSON.parse(this._dataFor(ann))));
	}


	///////////////////////////////////////////////////////////////////////////////////////////
	//  CALLBACKS
	///////////////////////////////////////////////////////////////////////////////////////////

	/*
	PUBLIC: Callback method for correctionCreated event.
	Receives an correction and sends a POST request to the sever using the URI for the "create" action.

	correction - An correction Object that was created.

	Examples:

	store.correctionCreated({text: "my new correction comment"})
	// Results in an HTTP POST request to the server containing the correction as serialised JSON.

	Returns nothing
	*/
	correctionCreated(correction) {
		// Pre-register the correction so as to save the list of highlight elements.
		if (!this.corrections.includes(correction)) {
			this.registerCorrection(correction);

			this._apiRequest('create', correction, data => {
				// Update with (e.g.) ID from server.
				if ((data.id == null)) {
					console.warn(Corrector._t("Warning: No ID returned from server for correction "), correction);
				}
				this.updateCorrection(correction, data);
			});
		}
		else {
			// This is called to update corrections created at load time with
			// the highlight elements created by Corrector.
			this.updateCorrection(correction, {});
		}
	}

	/*
	PUBLIC: Callback method for correctionUpdated event.
	Receives an correction and sends a PUT request to the sever using the URI for the "update" action.

	correction - An correction Object that was updated.

	Examples:

	store.correctionUpdated({id: "blah", text: "updated correction comment"})
	// Results in an HTTP PUT request to the server containing the correction as serialised JSON.

	Returns nothing
	*/
	correctionUpdated(correction) {
		if (this.corrections.includes(correction)) {
			this._apiRequest('update', correction, (data => this.updateCorrection(correction, data)));
		}
	}

	/*
	PUBLIC: Callback method for correctionDeleted event.
	Receives an correction and sends a DELETE request to the server using the URI for the destroy action.

	correction - An correction Object to resister.

	Examples:
	store.registerCorrection({id: "correction"})
	// Results in an HTTP DELETE request to the server.

	// Returns nothing.
	*/
	correctionDeleted(correction) {
		if (this.corrections.includes(correction)) {
			this._apiRequest('destroy', correction, (() => this.unregisterCorrection(correction)));
		}
	}


	/*
	Callback method for Store#loadCorrections().
	Processes the data returned from the server (a JSON array of correction Objects) and updates the registry as well as loading them into the Corrector.

	data - An Array of correction Objects

	Examples:
	console.log @correction # => []
	store._onLoadCorrections([{}, {}, {}])
	console.log @correction # => [{}, {}, {}]

	Returns nothing.
	*/
	_onLoadCorrections(data = []) {
		let a;
		const correctionMap = {};
		for (a of this.corrections) {
			correctionMap[a.id] = a;
		}

		const newData = [];
		for (a of data) {
			if (correctionMap[a.id]) {
				const correction = correctionMap[a.id];
				this.updateCorrection(correction, a);
			}
			else {
				newData.push(a);
			}
		}

		this.corrections = this.corrections.concat(newData);
		this.corrector.loadCorrections(newData.slice());
	}

	/*
	Callback method for Store#loadCorrectionsFromSearch().
	Processes the data returned from the server (a JSON array of correction Objects) and updates the registry as well as loading them into the Corrector.

	data - An Array of correction Objects

	Returns nothing.
	*/
	_onLoadCorrectionsFromSearch(data={}) {
		this._onLoadCorrections(data.rows || []);
	}

	/*
	Callback method for Store#loadCorrectionsFromSearch().
	Processes the data returned from the server (a JSON array of correction Objects) and updates the registry as well as loading them into the Corrector.
	Returns the jQuery XMLHttpRequest wrapper enabling additional callbacks to be applied as well as custom error handling.

	action    - The action String eg. "read", "search", "create", "update" or "destroy".
	obj       - The data to be sent, either correction object or query string.
	onSuccess - A callback Function to call on successful request.

	Examples:
	store._apiRequest('read', {id: 4}, (data) -> console.log(data))
	// Outputs the correction returned from the server.

	Returns jXMLHttpRequest object.
	*/
	_apiRequest(action, obj, onSuccess) {
		const id  = obj && obj.id;
		const url = this._urlFor(action, id);
		const options = this._apiRequestOptions(action, obj, onSuccess);

		const request = $.ajax(url, options);

		// Append the id and action to the request object
		// for use in the error callback.
		request._id = id;
		request._action = action;
		return request;
	}

	/*
	jQuery.ajax() callback. Displays an error notification to the user if the request failed.

	xhr - The jXMLHttpRequest object.

	Returns nothing.
	*/
	_onError(xhr) {
		const action  = xhr._action;
		let message = Corrector._t("Sorry we could not ") + action + Corrector._t(" this correction");

		if (xhr._action === 'search') {
			message = Corrector._t("Sorry we could not search the store for corrections");
		} else if ((xhr._action === 'read') && !xhr._id) {
			message = Corrector._t("Sorry we could not ") + action + Corrector._t(" the corrections from the store");
		}

		switch (xhr.status) {
			case 401: message = Corrector._t("Sorry you are not allowed to ") + action + Corrector._t(" this correction"); break;
			case 404: message = Corrector._t("Sorry we could not connect to the corrections store"); break;
			case 500: message = Corrector._t("Sorry something went wrong with the correction store"); break;
		}

		Corrector.showNotification(message, CorrectorNotification.ERROR);

		console.error(Corrector._t("API request failed:") + ` '${xhr.status}'`);
	}


}

// Register the plugin
CorrectorPlugin.Store = CorrectionsStorePlugin;
